import { Button } from '@components/button';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconCloseSmall } from '@components/icons/close-small';
import { IconCondition } from '@components/icons/condition';
import { IconEdit } from '@components/icons/edit';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { Sidebar, hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Tag } from '@components/tag';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import {
  useJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import {
  Condition,
  ConditionComparator,
  ConditionType,
  TravellerJourneyActionDetailed,
} from 'api/types';

import { getComparatorLabel, getValueLabel } from './description';
import { ConditionFormContent, useConjunctionLabel } from './helpers';

import {
  ActionProgressBar,
  EditableActionContent,
  SidebarProps,
  TravellerActionActions,
  useEditingContext,
} from '../common';

type ActionType = 'condition_v2';

export const SidebarContent = ({ shortId }: SidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);

  const travellerActionQuery = useJourneyTravellerAction<ActionType>(shortId);
  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !travellerActionQuery.data) {
    return <div>{t('dataNotFound')}</div>;
  }

  const action = travellerActionQuery.data;

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('condition')} icon={IconCondition} iconColor="teal100">
        <TravellerActionActions
          action={action}
          ratioOverride={action.status === 'completed' ? 1 : 0}
          statusOverride={action.status}
        />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <EditableActionContent
          edit={<FormContent action={action} />}
          display={<DetailsContent action={action} />}
        />
      </Sidebar.Content>
    </Sidebar>
  );
};

type ContentProps = {
  action: TravellerJourneyActionDetailed<ActionType>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { startEditing } = useEditingContext();
  const status = action.status;
  const progressText = status === 'completed' ? t('completed') : t('notStarted');

  if (!action.values) return null;

  const conditions = action.values?.conditions;
  const savedConditions = action.savedValues?.conditions;

  return (
    <Stack space={6}>
      <HideContainer title={t('actionStatus')}>
        <ActionProgressBar
          action={action}
          progressText={progressText}
          ratioOverride={status === 'completed' ? 1 : 0}
        />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <LabelTextGroup label={t('description')} text={action.description} />
      </HideContainer>
      <Sidebar.Separator />
      <HideContainer title={t('condition')}>
        <Stack space={4}>
          <Text
            font="regular14"
            text={styledTranslate({
              key: 'continueWhen',
              variables: {
                conditionType: {
                  text: action.values?.conditionType === 'all' ? t('all') : t('any'),
                  underline: true,
                },
              },
              translations: sidebarLocale,
            })}
          />
          <Stack space={1}>
            {conditions?.map((condition, idx) => (
              <ConditionInstance
                key={idx}
                condition={condition}
                savedCondition={savedConditions?.[idx]}
                conjunction={idx < conditions.length - 1}
                conditionType={action.values?.conditionType as ConditionType}
              />
            ))}
          </Stack>
          {status === 'completed' && (
            <>
              <div>
                <Tag
                  text={action.isHalted ? t('conditionNotMet') : t('conditionMet')}
                  icon={action.isHalted ? <IconCloseSmall /> : <IconCheckmarkSmall />}
                  size="large"
                  variant={action.isHalted ? 'red' : 'green'}
                />
              </div>
              <Text
                font="regular14"
                text={action.isHalted ? t('actionNotTriggered') : t('actionTriggered')}
              />
            </>
          )}
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <Stack orientation="horizontal" justify="space-between">
            <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
            <Button text={t('core.close')} variant="secondary" onClick={hideSidebar} />
          </Stack>
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type SavedCondition = {
  comparator: string;
  field: string;
  value: string;
  success?: boolean;
};

const ConditionInstance = ({
  condition,
  conjunction,
  conditionType,
  savedCondition,
}: {
  condition: Condition;
  conjunction: boolean;
  conditionType: ConditionType;
  savedCondition?: SavedCondition;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { field, comparator, value } = condition;
  const conjunctionLabel = useConjunctionLabel(conditionType);
  if (!field || !comparator) {
    return <Text font="regular14" text={t('dataNotFound')} underline />;
  }

  const valueLabel = getValueLabel(value, comparator);
  const savedValueLabel =
    getValueLabel(savedCondition?.field, savedCondition?.comparator) || 'Empty';

  const conditionElement = field?.variables && Object.values(field.variables)[0].label;
  const translationKey = comparator.includes('empty')
    ? 'conditionInstanceWithoutValue'
    : 'conditionInstance';

  return (
    <>
      <Stack space={0} orientation="horizontal" align="center" style={{ gap: pxToRem(8) }}>
        <Text
          font="semibold14"
          text={styledTranslate({
            key: translationKey,
            variables: {
              element: {
                text: conditionElement,
                color: 'elementBlue100',
                font: 'semibold14',
              },
              comparator: {
                text: getComparatorLabel(comparator),
                color: getComparatorColor(comparator),
                font: 'semibold14',
              },
              value: {
                text: valueLabel,
                color: 'textPrimary',
                font: 'semibold14',
              },
            },
            translations: sidebarLocale,
          })}
          style={{
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        />
        <ConditionStatusIcon success={savedCondition?.success} />
      </Stack>

      {savedCondition && (
        <Stack space={2} orientation="horizontal" align="center">
          <Text
            font="regular14"
            text={styledTranslate({
              key: 'recivedValue',
              variables: {
                value: {
                  text: savedValueLabel,
                  color: 'textPrimary',
                  font: 'regular14',
                },
              },
              translations: sidebarLocale,
            })}
            style={{
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          />
        </Stack>
      )}
      {conjunction && <Text font="semibold14" capitalize text={conjunctionLabel} />}
    </>
  );
};

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();

  const editMutation = useUpdateJourneyTravellerAction(action, { onSuccess: stopEditing });

  return (
    <ConditionFormContent
      action={action}
      conditionDescription={t('conditionDescription')}
      journeyId={action.journeyId}
      onCancel={stopEditing}
      hideActionButtons
      showDescriptionField
      isTraveller
      onSubmit={(data) =>
        editMutation.mutateAsync({
          automatic: true,
          action: 'condition_v2',
          values: data,
          description: data.description,
        })
      }
    />
  );
};

const ConditionStatusIcon = ({ success }: { success: boolean | undefined }) => {
  if (success === undefined) return <span />;
  return success ? (
    <IconCheckmark color="elementGreen100" />
  ) : (
    <IconCloseRegular color="elementRed100" />
  );
};

type ComparatorColor = 'elementGreen200' | 'elementRed100' | 'textPrimary';

const comparatorColorMap: Partial<Record<ConditionComparator, ComparatorColor>> = {
  eq: 'elementGreen200',
  contains: 'elementGreen200',
  starts_with: 'elementGreen200',
  ends_with: 'elementGreen200',
  file_contains: 'elementGreen200',
  file_starts_with: 'elementGreen200',
  file_ends_with: 'elementGreen200',
  select_eq: 'elementGreen200',
  select_multi_eq: 'elementGreen200',
  neq: 'elementRed100',
  select_neq: 'elementRed100',
  select_multi_neq: 'elementRed100',
  ncontains: 'elementRed100',
  file_ncontains: 'elementRed100',
  number_neq: 'elementRed100',
  number_lt: 'elementRed100',
  number_lte: 'elementRed100',
};

const getComparatorColor = (comparator: ConditionComparator): ComparatorColor => {
  return comparatorColorMap[comparator] ?? 'textPrimary';
};

const sidebarLocale = {
  en: {
    conditionDescription: 'Continues',
    condition: 'Condition',
    dataNotFound: 'Data not found',
    actionStatus: 'Action status',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    continueWhen: 'Continue when {conditionType} of the following matches',
    is: 'is',
    isNot: 'is not',
    any: 'any',
    all: 'all',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    conditionInstance: '{element} • {comparator} • {value}',
    conditionInstanceWithoutValue: '{element} • {comparator}',
    conditionMet: 'Condition met',
    conditionNotMet: 'Condition not met',
    recivedValue: 'Received value • {value}',
    actionTriggered: 'The following action was triggered',
    actionNotTriggered: 'The following action was not triggered',
    actionDetails: 'Action details',
    description: 'Description',
  },
};
